import { useState, useCallback } from "react";
import { decodeEntities } from "@/lib/sanitize";
import TiltCard from "@/components/TiltCard";
import {
  Bell,
  BellOff,
  Loader2,
  Settings,
  ChevronDown,
  ChevronUp,
  Radar,
  Search,
  ExternalLink,
  Trash2,
  Play,
  Pause,
} from "lucide-react";
import { useSavedTopics, SavedTopic } from "@/hooks/useSavedTopics";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import {
  RetroDialog,
  RetroDialogHeader,
  RetroDialogBody,
} from "@/components/ui/retro-dialog";
import EmptyTrackedTopics from "@/components/empty-states/EmptyTrackedTopics";
import AudioPlayer from "@/components/AudioPlayer";

interface MatchedEpisode {
  id: string;
  title: string;
  slug: string;
  published_at: string | null;
  audio_url: string | null;
  podcast: { name: string; artwork_url: string | null } | null;
  score: number; // 0-100 relevance
}

/* ───────────────────────── Settings Modal ───────────────────────── */

function TopicSettingsModal({
  topic,
  open,
  onOpenChange,
  onToggleActive,
  onToggleNotify,
  onUpdateQuery,
  onRemove,
}: {
  topic: SavedTopic;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onToggleActive: (id: string) => void;
  onToggleNotify: (id: string, current: boolean) => void;
  onUpdateQuery: (id: string, query: string) => Promise<boolean>;
  onRemove: (id: string) => void;
}) {
  const [editingQuery, setEditingQuery] = useState(false);
  const [queryValue, setQueryValue] = useState(topic.query);

  const handleSaveQuery = async () => {
    const trimmed = queryValue.trim();
    if (!trimmed || trimmed === topic.query) {
      setEditingQuery(false);
      setQueryValue(topic.query);
      return;
    }
    const ok = await onUpdateQuery(topic.id, trimmed);
    if (ok) {
      toast.success("Topic updated");
      setEditingQuery(false);
    } else {
      toast.error("Could not update — duplicate or invalid");
    }
  };

  const handleDelete = () => {
    onRemove(topic.id);
    onOpenChange(false);
  };

  return (
    <RetroDialog open={open} onOpenChange={onOpenChange} size="md">
      <RetroDialogHeader
        title="Topic Settings"
        description="Manage how this tracked topic works."
      />
      <RetroDialogBody className="space-y-5">
          {/* ── Topic Query ── */}
          <div>
            <label className="font-mono text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Topic / Question
            </label>
            {editingQuery ? (
              <div className="space-y-2">
                <textarea
                  autoFocus
                  value={queryValue}
                  onChange={(e) => setQueryValue(e.target.value)}
                  rows={2}
                  className="textarea-retro min-h-[60px]"
                />
                <p className="font-mono text-[10px] text-muted-foreground">
                  ⚠️ Changing this will update which future episodes are matched. Past matches remain.
                </p>
                <div className="flex gap-2">
                  <button onClick={handleSaveQuery} className="btn-comic-primary btn-comic-sm">
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setQueryValue(topic.query);
                      setEditingQuery(false);
                    }}
                    className="btn-comic-ghost btn-comic-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setQueryValue(topic.query);
                  setEditingQuery(true);
                }}
                className="w-full text-left font-retro font-bold text-sm p-2.5 retro-border bg-retro-amber/15 hover:bg-retro-amber/25 transition-colors group rounded-md"
              >
                "{topic.query}"
                <span className="font-mono text-[10px] text-muted-foreground ml-2">
                  click to edit
                </span>
              </button>
            )}
          </div>

          {/* ── Tracking Status ── */}
          <div className="retro-border p-3 bg-retro-amber/15 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-mono font-bold text-xs mb-0.5">Episode Scanning</p>
                {topic.is_active ? (
                  <p className="font-mono text-[11px] text-muted-foreground">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5 align-middle" />
                    Tracking is <strong className="text-foreground">on</strong>. New episodes matching your topic are added automatically.
                  </p>
                ) : (
                  <p className="font-mono text-[11px] text-muted-foreground">
                    <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground mr-1.5 align-middle" />
                    Tracking is <strong className="text-foreground">off</strong>. No new episodes will be matched.
                  </p>
                )}
              </div>
              <button
                onClick={() => onToggleActive(topic.id)}
                className={
                  topic.is_active
                    ? "btn-comic-secondary btn-comic-sm flex-shrink-0 ml-3"
                    : "btn-comic-primary btn-comic-sm flex-shrink-0 ml-3"
                }
              >
                {topic.is_active ? "Pause" : "Resume"}
              </button>
            </div>
          </div>

          {/* ── Email Notifications ── */}
          <div className="retro-border p-3 bg-retro-amber/15 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-mono font-bold text-xs mb-0.5">Email Notifications</p>
                {topic.notify_email ? (
                  <p className="font-mono text-[11px] text-muted-foreground">
                    <Bell className="w-3 h-3 inline mr-1 text-primary align-middle" />
                    <strong className="text-foreground">On</strong> — You'll get an email when a closely matching episode is found.
                  </p>
                ) : (
                  <p className="font-mono text-[11px] text-muted-foreground">
                    <BellOff className="w-3 h-3 inline mr-1 align-middle" />
                    <strong className="text-foreground">Off</strong> — Matches are tracked silently. No emails sent.
                  </p>
                )}
              </div>
              <button
                onClick={() => onToggleNotify(topic.id, topic.notify_email)}
                className={
                  topic.notify_email
                    ? "btn-comic-secondary btn-comic-sm flex-shrink-0 ml-3"
                    : "btn-comic-primary btn-comic-sm flex-shrink-0 ml-3"
                }
              >
                {topic.notify_email ? "Turn Off" : "Turn On"}
              </button>
            </div>
          </div>

          {/* ── Delete ── */}
          <div className="pt-2 border-t-2 border-foreground/20">
            <button
              onClick={handleDelete}
              className="btn-comic-danger btn-comic-sm w-full"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Tracked Topic
            </button>
            <p className="font-mono text-[10px] text-muted-foreground text-center mt-1.5">
              This will stop tracking and remove all match history.
            </p>
          </div>
      </RetroDialogBody>
    </RetroDialog>
  );
}

/* ───────────────────────── Topic Card ───────────────────────── */

function TopicCard({
  topic,
  onRemove,
  onToggleNotify,
  onToggleActive,
  onUpdateQuery,
}: {
  topic: SavedTopic;
  onRemove: (id: string) => void;
  onToggleNotify: (id: string, current: boolean) => void;
  onToggleActive: (id: string) => void;
  onUpdateQuery: (id: string, query: string) => Promise<boolean>;
}) {
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [matches, setMatches] = useState<MatchedEpisode[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  const isPaused = !topic.is_active;
  const [playingId, setPlayingId] = useState<string | null>(null);

  const loadMatches = useCallback(async () => {
    if (matches.length > 0 || loadingMatches) return;
    setLoadingMatches(true);
    try {
      const { data, error } = await supabase.functions.invoke("semantic-search", {
        body: { query: topic.query, source: "saved-topic-check" },
      });
      if (!error && data?.results) {
        setMatches(
          data.results.slice(0, 10).map((r: any) => ({
            id: r.id || r.episode_id,
            title: r.episodeTitle || r.title || "Unknown episode",
            slug: r.slug || r.id,
            published_at: r.publishDate || r.published_at || null,
            audio_url: r.audioUrl || r.audio_url || null,
            podcast: (r.podcastName || r.podcast)
              ? {
                  name: r.podcastName || r.podcast?.name || "Unknown",
                  artwork_url: r.podcastArtwork || r.podcast?.artwork_url || null,
                }
              : null,
            score: typeof r.confidenceScore === "number" ? r.confidenceScore : 0,
          }))
        );
      }
    } catch {
      // silent
    }
    setLoadingMatches(false);
  }, [topic.query, matches.length, loadingMatches]);

  const toggleExpand = () => {
    if (!expanded) loadMatches();
    setExpanded(!expanded);
  };

  return (
    <>
      <TiltCard
        className={`retro-border retro-shadow-sm bg-card overflow-hidden hover-lift transition-opacity ${
          isPaused ? "opacity-60" : ""
        }`}
      >
        {/* Main row */}
        <div className="p-3 sm:p-4 flex items-start gap-3">
          {/* Status dot */}
          <div className="flex-shrink-0 mt-1.5">
            <div
              className={`w-2.5 h-2.5 rounded-full ${
                isPaused ? "bg-muted-foreground" : "bg-green-500 animate-pulse"
              }`}
              title={isPaused ? "Paused" : "Actively scanning"}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-retro font-bold text-sm sm:text-base leading-tight mb-1 truncate">
              "{topic.query}"
            </h3>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[10px] sm:text-[11px] text-muted-foreground">
              <span>
                Tracked{" "}
                {new Date(topic.created_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={toggleExpand}
                className="text-primary font-bold hover:underline"
              >
                {matches.length > 0
                  ? `${matches.length} episode${matches.length !== 1 ? "s" : ""} match`
                  : topic.match_count > 0
                  ? `${topic.match_count} match${topic.match_count !== 1 ? "es" : ""}`
                  : expanded
                  ? "Hide episodes"
                  : "Show matching episodes"}
              </button>
              {topic.last_matched_at && (
                <span>
                  Last match{" "}
                  {new Date(topic.last_matched_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              )}
              {isPaused && (
                <span className="text-amber-600 font-bold">Paused</span>
              )}
            </div>
          </div>

          {/* Controls: just gear + expand */}
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              onClick={() => setSettingsOpen(true)}
              className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              title="Topic settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            <button
              onClick={toggleExpand}
              className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              title={expanded ? "Hide matching episodes" : "Show matching episodes"}
              aria-expanded={expanded}
            >
              {expanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Expanded matches */}
        {expanded && (
          <div className="border-t-2 border-border bg-retro-amber/15 px-3 sm:px-4 py-3">
            {loadingMatches ? (
              <div className="flex items-center gap-2 justify-center py-3">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                <span className="font-mono text-xs text-muted-foreground">Finding matches…</span>
              </div>
            ) : matches.length === 0 ? (
              <p className="font-mono text-xs text-muted-foreground text-center py-2">
                No matching episodes found yet. We'll keep scanning!
              </p>
            ) : (
              <div className="space-y-2">
                <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mb-2">
                  Top matches
                </p>
                {matches.map((ep) => {
                  const isPlaying = playingId === ep.id;
                  return (
                  <div
                    key={ep.id}
                    className="retro-border bg-card hover-lift transition-all overflow-hidden"
                  >
                    <div className="flex items-center gap-2.5 p-2">
                      {ep.podcast?.artwork_url ? (
                        <img
                          src={ep.podcast.artwork_url}
                          alt=""
                          className="w-8 h-8 retro-border object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 retro-border bg-muted flex items-center justify-center flex-shrink-0">
                          <span className="font-retro text-xs text-muted-foreground">?</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-retro font-bold text-xs leading-tight truncate">
                          <Link to={`/episode/${ep.slug}`} className="hover:text-primary transition-colors hover:underline">
                            {decodeEntities(ep.title)}
                          </Link>
                        </p>
                        <p className="font-mono text-[10px] text-muted-foreground truncate">
                          {ep.podcast?.name || "Unknown podcast"}
                          {ep.published_at && (
                            <>
                              {" · "}
                              {new Date(ep.published_at).toLocaleDateString("en-US", {
                                month: "short",
                                year: "numeric",
                              })}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {ep.score > 0 && (
                          <span
                            className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded retro-border ${
                              ep.score >= 85
                                ? "bg-green-100 text-green-800 border-green-700"
                                : ep.score >= 70
                                ? "bg-amber-100 text-amber-800 border-amber-700"
                                : "bg-muted text-muted-foreground"
                            }`}
                            title="Relevance score"
                          >
                            {Math.round(ep.score)}%
                          </span>
                        )}
                        {ep.audio_url && (
                          <button
                            onClick={() => setPlayingId(isPlaying ? null : ep.id)}
                            className={`comic-play-btn relative overflow-hidden flex-shrink-0 w-8 h-8 border-[2.5px] border-[hsl(195,80%,28%)] rounded-md bg-[hsl(195,85%,55%)] text-white flex items-center justify-center transition-all duration-150 transform shadow-[2px_2px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[1px] hover:translate-y-[1px] active:scale-90 ${
                              isPlaying ? "" : "rotate-[-2deg] hover:rotate-0"
                            }`}
                            title={isPlaying ? "Pause" : "Play episode"}
                            aria-label={isPlaying ? "Pause episode" : "Play episode"}
                          >
                            {isPlaying ? (
                              <Pause className="w-3.5 h-3.5 relative z-10" strokeWidth={3} />
                            ) : (
                              <Play className="w-3.5 h-3.5 ml-0.5 relative z-10" strokeWidth={3} fill="currentColor" />
                            )}
                          </button>
                        )}
                        <Link
                          to={`/episode/${ep.slug}`}
                          className="p-1 text-muted-foreground hover:text-foreground"
                          title="Open episode"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                    {isPlaying && ep.audio_url && (
                      <div className="border-t-2 border-primary/30 p-2 bg-background">
                        <AudioPlayer
                          src={ep.audio_url}
                          title={ep.title}
                          podcastName={ep.podcast?.name || undefined}
                          artworkUrl={ep.podcast?.artwork_url || undefined}
                          episodeId={ep.id}
                          autoPlay
                        />
                      </div>
                    )}
                  </div>
                  );
                })}
                <button
                  onClick={() => navigate(`/?q=${encodeURIComponent(topic.query)}`)}
                  className="w-full font-mono text-xs text-primary font-bold hover:underline py-1 text-center"
                >
                  View all results →
                </button>
              </div>
            )}
          </div>
        )}
      </TiltCard>

      {/* Settings modal */}
      <TopicSettingsModal
        topic={topic}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onToggleActive={onToggleActive}
        onToggleNotify={onToggleNotify}
        onUpdateQuery={onUpdateQuery}
        onRemove={onRemove}
      />
    </>
  );
}

/* ───────────────────────── Main Component ───────────────────────── */

export default function TrackedTopics() {
  const { topics, loading, removeTopic, toggleNotify, toggleActive, updateQuery } =
    useSavedTopics();
  const navigate = useNavigate();

  const handleRemove = async (id: string) => {
    await removeTopic(id);
    toast.success("Topic removed");
  };

  const handleToggleNotify = async (id: string, currentlyOn: boolean) => {
    await toggleNotify(id);
    toast.success(currentlyOn ? "Email notifications off" : "Email notifications on");
  };

  const handleToggleActive = async (id: string) => {
    const topic = topics.find((t) => t.id === id);
    await toggleActive(id);
    toast.success(topic?.is_active ? "Topic paused" : "Topic resumed");
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="font-mono text-xs text-muted-foreground">Loading topics…</span>
      </div>
    );
  }

  return (
    <div>
      {/* Section header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Radar className="w-4 h-4 text-primary" />
          <p className="font-mono text-xs text-muted-foreground">
            We scan new episodes daily and notify you when we find matches.
          </p>
        </div>
      </div>

      {topics.length === 0 ? (
        <EmptyTrackedTopics />
      ) : (
        <div className="space-y-2">
          {topics.map((t) => (
            <TopicCard
              key={t.id}
              topic={t}
              onRemove={handleRemove}
              onToggleNotify={handleToggleNotify}
              onToggleActive={handleToggleActive}
              onUpdateQuery={updateQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
}
